import { JSX } from 'react';

/**
 * When a replayed card was first put on screen.
 *
 * HQ-11. A resumed conversation now brings back what it showed as well as what it said - the
 * maps, the "Open in HQ" buttons, the field proposals. Those are pictures of where a record was
 * at the time, and a conversation can be up to a fortnight old, so replaying one undated would
 * put stale data on screen as though it were current. That is the exact behaviour stripping tool
 * traffic from the stored conversation exists to prevent.
 *
 * So a replayed card says when it was taken, and its actions are dead. This is the saying half;
 * `isReplayed` below is what the cards use for the other.
 */
export interface CapturedStampProps {
    /** Unix seconds. */
    capturedAt: number;
}

/**
 * Whether this transcript entry is a record rather than a live view.
 *
 * One reader, so a new card cannot forget to ask.
 *
 * @param capturedAt the entry's capture time, if it has one
 */
export function isReplayed(capturedAt?: number): boolean {
    return typeof capturedAt === 'number' && capturedAt > 0;
}

/**
 * How long ago, in words, with the exact time on hover.
 *
 * Relative rather than absolute because the question a reader is asking is "can I still trust
 * this?", and "12 days ago" answers it where "28 August, 14:03" makes them do the arithmetic.
 *
 * @param capturedAt unix seconds
 * @param now the clock, injectable for the tests
 */
export function capturedLabel(capturedAt: number, now: number = Date.now()): string {
    const seconds: number = Math.max(0, Math.round(now / 1000) - capturedAt);
    const minutes: number = Math.floor(seconds / 60);
    const hours: number = Math.floor(minutes / 60);
    const days: number = Math.floor(hours / 24);

    if (days >= 1) {
        return days === 1 ? 'shown yesterday' : `shown ${days} days ago`;
    }
    if (hours >= 1) {
        return hours === 1 ? 'shown an hour ago' : `shown ${hours} hours ago`;
    }
    if (minutes >= 1) {
        return minutes === 1 ? 'shown a minute ago' : `shown ${minutes} minutes ago`;
    }

    return 'shown a moment ago';
}

export const CapturedStamp: (props: CapturedStampProps) => JSX.Element | null =
    ({ capturedAt }: CapturedStampProps): JSX.Element | null => {
        if (!isReplayed(capturedAt)) {
            return null;
        }

        return (
            <span
                className="chat__captured"
                title={ new Date(capturedAt * 1000).toLocaleString() }
            >
                { capturedLabel(capturedAt) }
            </span>
        );
    };
